// Public SEO pages for the Bishkek 2050 genplan: a zones hub and one page per
// city zone, in Russian (no prefix) and Kyrgyz (/ky/…) — the two languages the
// official explanatory note exists in. Slugs live in genplan/data/zone-slugs.json
// so the sitemap and prerender scripts produce the very same URLs.
import slugsJson from '../../../../genplan/data/zone-slugs.json';
import { META, ZONES, type Zone } from './config';

export type SeoLang = 'ru' | 'ky';

export const MAP_PATH = '/turaq-kg-genplan-bishkek2050';
export const SITE = 'https://turaq.kg';
export const OFFICIAL_SOURCE_URL = 'https://genplan.bishkek.gov.kg';

const SLUGS = slugsJson as Record<string, string>;
export const zoneSlug = (id: string): string | undefined => SLUGS[id];
export const zoneBySlug = (slug: string): Zone | undefined => ZONES.find((z) => SLUGS[z.id] === slug);

/** City zones that get a page (outside-the-city territories have no genplan text). */
export const SEO_ZONES = ZONES.filter((z) => z.layer === 'city' && SLUGS[z.id]);
export const OUTSIDE_ZONES = ZONES.filter((z) => z.layer === 'outside');

const prefix = (lang: SeoLang) => (lang === 'ky' ? '/ky' : '');
export const hubPath = (lang: SeoLang) => `${prefix(lang)}${MAP_PATH}/zony`;
export const zonePath = (z: Zone, lang: SeoLang) => `${prefix(lang)}${MAP_PATH}/zona/${SLUGS[z.id]}`;
/** The interactive map showing only this zone (GenplanPage reads ?zone=). */
export const zoneOnMapPath = (z: Zone) => `${MAP_PATH}?zone=${encodeURIComponent(z.id)}`;
export const seoLangFromPath = (pathname: string): SeoLang => (pathname.split('/')[1] === 'ky' ? 'ky' : 'ru');

/** ru + ky alternates (+ x-default → ru) for a page path builder. */
export const hreflangs = (path: (lang: SeoLang) => string) => [
  { hreflang: 'ru-KG', href: SITE + path('ru') },
  { hreflang: 'ky-KG', href: SITE + path('ky') },
  { hreflang: 'x-default', href: SITE + path('ru') },
];

/** Groups for the hub and the «related zones» block. */
export const ZONE_GROUPS: { id: string; zones: string[] }[] = [
  { id: 'housing', zones: ['izh', 'malo', 'sredne', 'mnogo', 'vysot'] },
  { id: 'public', zones: ['mfod', 'spec_obsh'] },
  { id: 'industry', zones: ['prom', 'inzh', 'transp', 'spec_naz', 'rezhim'] },
  { id: 'green', zones: ['rekr', 'ozelen', 'otdyh', 'ozel_spec', 'pitomn', 'kladb'] },
  { id: 'other', zones: ['selhoz', 'akvat', 'ne_ust'] },
];
export const groupOf = (z: Zone) => ZONE_GROUPS.find((g) => g.zones.includes(z.id));
export const HOUSING = new Set(ZONE_GROUPS[0].zones);

export function areaChange(z: Zone): { existing: number | null; plan: number | null; pct: number | null } {
  const existing = z.area_official_ha?.existing ?? null;
  const plan = z.area_official_ha?.plan_2050 ?? null;
  const pct = existing && plan != null ? Math.round(((plan - existing) / existing) * 100) : null;
  return { existing, plan, pct };
}

export const drawnArea = (z: Zone) => META.zones[z.id]?.area_ha ?? null;

// ru-RU grouping for both languages: browsers lack ky-KG data and fall back to «1,798».
export const fmtHa = (n: number, _lang: SeoLang) => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);

const clip = (s: string, max: number) => (s.length <= max ? s : `${s.slice(0, max - 1).replace(/\s+\S*$/, '')}…`);

// ─── Copy (RU from the official note; KY mirrors it) ─────────────────────────

export const T = {
  ru: {
    home: 'Главная',
    map: 'Генплан Бишкека 2050',
    zones: 'Зоны генплана',
    hubTitle: 'Генплан Бишкека 2050: все функциональные зоны',
    hubLead:
      'Генеральный план города Бишкек до 2050 года делит территорию города на функциональные зоны: жилые, общественно-деловые, производственные, рекреационные и другие. Ниже — все зоны основного чертежа с описаниями из пояснительной записки и площадями «сейчас → 2050». Любой адрес можно проверить на интерактивной карте Turaq.kg.',
    statZones: 'функциональных зон',
    statAccuracy: 'точность привязки карты',
    statPoints: 'опорных точек привязки',
    openMap: 'Открыть карту генплана',
    checkPlot: 'Проверить участок по адресу',
    showOnMap: 'Показать зону на карте',
    now: 'Сейчас',
    plan: 'К 2050 году',
    change: 'Изменение',
    drawn: 'На чертеже',
    ha: 'га',
    polygons: 'участков на чертеже',
    whatGenplanSays: 'Что говорит генплан',
    includes: 'Включает',
    noText: 'Отдельного описания этой зоны в пояснительной записке нет — показаны площадь и расположение на чертеже.',
    pzzTitle: 'Генплан — не градостроительный регламент',
    pzz:
      'Генплан задаёт функциональное назначение территории. Конкретные параметры застройки — этажность, разрешённые и запрещённые виды использования — устанавливают Правила землепользования и застройки (ПЗЗ). Перед покупкой участка или стройкой сверяйтесь с официальными документами мэрии.',
    sources: 'Источники',
    officialSite: 'Официальный сайт генплана',
    related: 'Похожие зоны',
    allZones: 'Все зоны генплана',
    realEstate: 'Недвижимость в Бишкеке',
    flats: 'Квартиры в Бишкеке',
    houses: 'Дома в Бишкеке',
    plots: 'Участки в Бишкеке',
    outsideTitle: 'Территории за границей города',
    outsideLead: 'Пригородные территории на чертеже генплана — без описаний в пояснительной записке.',
    faqTitle: 'Частые вопросы',
    groups: {
      housing: 'Жилые зоны',
      public: 'Общественно-деловые зоны',
      industry: 'Производство и инфраструктура',
      green: 'Озеленение, отдых и особые территории',
      other: 'Другие зоны',
    } as Record<string, string>,
    zoneSuffix: 'генплан Бишкека 2050',
    floors: 'Этажность по легенде',
  },
  ky: {
    home: 'Башкы бет',
    map: 'Бишкектин башкы планы 2050',
    zones: 'Башкы пландын зоналары',
    hubTitle: 'Бишкектин башкы планы 2050: бардык функционалдык зоналар',
    hubLead:
      'Бишкек шаарынын 2050-жылга чейинки башкы планы шаардын аймагын функционалдык зоналарга бөлөт: турак жай, коомдук-ишкердик, өндүрүштүк, рекреациялык жана башкалар. Төмөндө негизги чиймедеги бардык зоналар түшүндүрмө каттагы сүрөттөмөлөрү жана «азыр → 2050» аянттары менен берилген. Каалаган даректи Turaq.kg интерактивдүү картасынан текшерсеңиз болот.',
    statZones: 'функционалдык зона',
    statAccuracy: 'картанын байланыштыруу тактыгы',
    statPoints: 'таяныч чекит',
    openMap: 'Башкы пландын картасын ачуу',
    checkPlot: 'Жер тилкесин дарек боюнча текшерүү',
    showOnMap: 'Зонаны картадан көрсөтүү',
    now: 'Азыр',
    plan: '2050-жылга карата',
    change: 'Өзгөрүү',
    drawn: 'Чиймеде',
    ha: 'га',
    polygons: 'чиймедеги участок',
    whatGenplanSays: 'Башкы план эмне дейт',
    includes: 'Камтыйт',
    noText: 'Бул зонанын түшүндүрмө катта өзүнчө сүрөттөмөсү жок — аянты жана чиймедеги жайгашуусу көрсөтүлдү.',
    pzzTitle: 'Башкы план — шаар куруу регламенти эмес',
    pzz:
      'Башкы план аймактын функционалдык багытын аныктайт. Курулуштун так параметрлерин — кабаттуулукту, уруксат берилген жана тыюу салынган пайдалануу түрлөрүн — Жерди пайдалануу жана курулуш эрежелери (ЖПКЭ/ПЗЗ) белгилейт. Жер тилкесин сатып алуудан же куруудан мурун мэриянын расмий документтери менен текшериңиз.',
    sources: 'Булактар',
    officialSite: 'Башкы пландын расмий сайты',
    related: 'Окшош зоналар',
    allZones: 'Башкы пландын бардык зоналары',
    realEstate: 'Бишкектеги кыймылсыз мүлк',
    flats: 'Бишкектеги батирлер',
    houses: 'Бишкектеги үйлөр',
    plots: 'Бишкектеги жер тилкелери',
    outsideTitle: 'Шаардын чегинен тышкаркы аймактар',
    outsideLead: 'Башкы пландын чиймесиндеги шаар четиндеги аймактар — түшүндүрмө катта сүрөттөмөсү жок.',
    faqTitle: 'Көп берилүүчү суроолор',
    groups: {
      housing: 'Турак жай зоналары',
      public: 'Коомдук-ишкердик зоналар',
      industry: 'Өндүрүш жана инфраструктура',
      green: 'Жашылдандыруу, эс алуу жана өзгөчө аймактар',
      other: 'Башка зоналар',
    } as Record<string, string>,
    zoneSuffix: 'Бишкектин башкы планы 2050',
    floors: 'Шарттуу белги боюнча кабаттуулук',
  },
} satisfies Record<SeoLang, unknown>;

/** FAQ — only what the genplan and our map actually say. */
export function faq(lang: SeoLang): { q: string; a: string }[] {
  const acc = META.accuracy_label_m;
  const gcps = META.accuracy_m.gcps;
  if (lang === 'ky') {
    return [
      {
        q: 'Бишкектин 2050-жылга чейинки башкы планы деген эмне?',
        a: 'Бул шаардын 2050-жылга чейинки өнүгүүсүн аныктаган расмий документ: аймактын функционалдык зоналары, транспорттук жана инженердик инфраструктура. Негизги чийменин масштабы 1:10 000; документтер genplan.bishkek.gov.kg сайтында жарыяланган.',
      },
      {
        q: 'Жер тилкем кайсы зонага кирерин кантип билем?',
        a: 'Turaq.kg картасын ачып, даректи же координаттарды киргизиңиз, же картанын керектүү жерин басыңыз — зонанын аталышы, сүрөттөмөсү жана булагы көрсөтүлөт. Жер тилкесин чийип, ал кайсы зоналарга бөлүнөрүн да көрсө болот.',
      },
      {
        q: 'Башкы план менен ЖПКЭ (ПЗЗ) эмнеси менен айырмаланат?',
        a: 'Башкы план аймактын функционалдык багытын аныктайт. Кабаттуулук жана уруксат берилген пайдалануу түрлөрү Жерди пайдалануу жана курулуш эрежелеринде белгиленет — алар башкы пландын бөлүгү эмес.',
      },
      {
        q: 'Карта канчалык так?',
        a: `Чийме ${gcps} таяныч чекит боюнча байланыштырылган, тактыгы болжол менен ±${acc} м. Укуктук маанилүү чечимдер үчүн мэриянын расмий документтерин текшериңиз.`,
      },
    ];
  }
  return [
    {
      q: 'Что такое генплан Бишкека до 2050 года?',
      a: 'Это официальный документ, который определяет развитие города до 2050 года: функциональные зоны территории, транспортную и инженерную инфраструктуру. Основной чертёж выполнен в масштабе 1:10 000; документы опубликованы на сайте genplan.bishkek.gov.kg.',
    },
    {
      q: 'Как узнать, в какую зону генплана попадает мой участок?',
      a: 'Откройте карту генплана на Turaq.kg, введите адрес или координаты либо нажмите на нужное место — появятся название зоны, описание из пояснительной записки и источник. Можно также нарисовать участок и увидеть, на какие зоны он делится.',
    },
    {
      q: 'Чем генплан отличается от ПЗЗ?',
      a: 'Генплан задаёт функциональное назначение территории. Этажность, разрешённые и запрещённые виды использования устанавливают Правила землепользования и застройки (ПЗЗ) — в генплан они не входят.',
    },
    {
      q: 'Насколько точна карта?',
      a: `Чертёж привязан по ${gcps} опорным точкам, точность — около ±${acc} м. Для юридически значимых решений сверяйтесь с официальными документами мэрии.`,
    },
  ];
}

export function hubMeta(lang: SeoLang) {
  return lang === 'ky'
    ? {
        title: 'Бишкектин башкы планы 2050: бардык зоналар жана аянттар | TURAQ.KG',
        description:
          'Бишкектин 2050-жылга чейинки башкы планынын бардык функционалдык зоналары: түшүндүрмө каттагы сүрөттөмөлөр, «азыр → 2050» аянттары жана интерактивдүү карта.',
      }
    : {
        title: 'Генплан Бишкека 2050: все зоны, описания и площади | TURAQ.KG',
        description:
          'Все функциональные зоны генплана Бишкека до 2050 года: описания из пояснительной записки, площади «сейчас → 2050», проверка участка на интерактивной карте.',
      };
}

export function zoneMeta(z: Zone, lang: SeoLang) {
  const name = (lang === 'ky' ? z.name.ky : null) || z.name.ru;
  const text = (lang === 'ky' ? z.description_ky : null) || z.description || '';
  const { existing, plan } = areaChange(z);
  const area =
    existing != null && plan != null
      ? lang === 'ky'
        ? ` Аянты: ${fmtHa(existing, lang)} га → ${fmtHa(plan, lang)} га (2050).`
        : ` Площадь: ${fmtHa(existing, lang)} га → ${fmtHa(plan, lang)} га к 2050 году.`
      : '';
  return {
    name,
    title: `${clip(name, 60)} — ${T[lang].zoneSuffix} | TURAQ.KG`,
    description: clip(`${text}${area}`.trim() || `${name} — ${T[lang].zoneSuffix}.`, 158),
  };
}
